import { injectable } from 'inversify';
import 'reflect-metadata';
import { Response } from '@/_shared/_api/models/response.model';
import { SignUp } from '../_api/models/signup.model';
import { PasswordSigninResult } from '../models/login/login.model';
import { signOut } from 'next-auth/react';
import jwt, { JwtPayload } from 'jsonwebtoken';
import axiosClient, { ejectAxiosInterceptor } from './axios-client';
import { ERRORS } from '../constants';
import { User, UserDetails } from '../models/users/user.model';
import { UpdateUserDetailsRequest } from '../models/users/edit-profile.model';
import {
  ResetPasswordRequest,
  InternalResetPasswordRequest,
} from '../_api/models/reset-password.model';
// import LogHelper from '../helpers/log.helper';
import { queryClient } from '../helpers/react-query.helper';
import { ApiResponse } from '../utils/api-response.utils';
import { authBrowserStorage } from '../utils/browser-storage.utils';

@injectable()
export class IdentityService {
  async signUp(
    values: SignUp
  ): Promise<ApiResponse<PasswordSigninResult | string | null>> {
    try {
      const response = await axiosClient.post<
        ApiResponse<PasswordSigninResult | string | null>
      >('/api/sign-up', values, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch {
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async verifyEmail(token: string): Promise<ApiResponse<any>> {
    const response = await axiosClient.put(
      `/api/email/verify-email?token=${token}`
    );
    return response.data;
  }

  // Not currently in use
  // async validateAccount(id: number): Promise<any> {
  //   const apiResponse = {
  //     data: {} as any,
  //     errorCode: "",
  //   };
  //   try {
  //     const response = await fetch(
  //       "/api/email/update-account-verification/" + id,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //       }
  //     );

  //     apiResponse.data = await response.json();
  //   } catch (error) {
  //     this.logger.postLogToCloudWatch(
  //       `Error in creating user in Cube Asia: ${JSON.stringify(error)}`,
  //       0
  //     );
  //     apiResponse.errorCode = error as string;
  //   }
  //   return apiResponse;
  // }

  async getUserDetails(id: number): Promise<ApiResponse<UserDetails>> {
    try {
      const response = await axiosClient.get<ApiResponse<UserDetails>>(
        '/api/my-account/get-user-details/' + id
      );

      return response.data;
    } catch {
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getUserById(id: number): Promise<ApiResponse<User | null>> {
    try {
      const response = await axiosClient.get<ApiResponse<User | null>>(
        '/api/user/' + id
      );

      return response.data;
    } catch {
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async updateUserDetails(
    request: UpdateUserDetailsRequest,
    file: any
  ): Promise<ApiResponse<UserDetails | null>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('request', JSON.stringify(request));

    const response = await axiosClient.post('/api/my-account/edit', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  async updateUserPassword(
    values: ResetPasswordRequest
  ): Promise<ApiResponse<boolean | null>> {
    try {
      const response = await axiosClient.post<ApiResponse<boolean | null>>(
        '/api/my-account/reset-password/',
        values
      );

      return response.data;
    } catch {
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async updateInternalUserPassword(
    values: InternalResetPasswordRequest
  ): Promise<ApiResponse<boolean | null>> {
    const response = await axiosClient.post<ApiResponse<boolean | null>>(
      '/api/my-account/internal-reset-password/',
      values
    );

    return response.data;
  }

  async logout(redirect?: string) {
    // Stamps `users.tokens_valid_from`, which rejects every token carrying an
    // older `loginAt` claim. Runs before the local teardown, while the
    // session is still active.
    await axiosClient.post('/api/auth/logout').catch(() => undefined);

    queryClient.clear();
    ejectAxiosInterceptor();
    authBrowserStorage.clear();
    await signOut({ redirect: !!redirect, callbackUrl: redirect });
  }

  async isTokenExpired(token: string) {
    const currentTime = Date.now();

    const decodedToken = (await jwt.decode(token)) as JwtPayload;
    return currentTime > decodedToken.exp!;
  }

  async validateCubeAsiaAccount(id: number): Promise<any> {
    try {
      const response = await axiosClient.post<Response<any> | null>(
        '/api/auth/cube-asia/validate',
        { id: id }
      );
      return response;
    } catch {
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async updateUserOnboardingStatus(
    id: number
  ): Promise<ApiResponse<boolean | null>> {
    try {
      const response = await axiosClient.post<ApiResponse<boolean | null>>(
        '/api/user/update-onboarding/' + id
      );
      return response.data;
    } catch {
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async acceptUserConsent(
    id: number,
    token: string,
    isUserConsentAccepted: boolean,
    isUserTrackingEnabled: boolean
  ): Promise<ApiResponse<boolean | null>> {
    try {
      const response = await axiosClient.put<ApiResponse<boolean | null>>(
        '/api/user/' + id + '/update-consent',
        {
          isUserConsentAccepted,
          isUserTrackingEnabled,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch {
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async updateUserConsent(
    id: number,
    isUserConsentAccepted: boolean,
    isUserTrackingEnabled: boolean
  ): Promise<ApiResponse<boolean | null>> {
    try {
      const response = await axiosClient.put<ApiResponse<boolean | null>>(
        '/api/user/' + id + '/update-consent',
        {
          isUserConsentAccepted,
          isUserTrackingEnabled,
        }
      );
      return response.data;
    } catch {
      throw new Error(ERRORS.unexpectedError);
    }
  }
}
